import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './App.css';
import logger from './utils/logger';

function DepositTable() {
  const getYesterdayDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [selectedDate, setSelectedDate] = useState(getYesterdayDate());
  const [isInitializingDate, setIsInitializingDate] = useState(true);
  const [inputDataList, setInputDataList] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settlementStatuses, setSettlementStatuses] = useState({});
  const [currentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch (e) {
      return null;
    }
  });

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super': return '슈퍼어드민';
      case 'admin': return '본사';
      case 'dev': return '개발';
      case 'chief_major': return '총대본';
      case 'representative': return '대표자';
      case 'major_branch': return '대본사';
      case 'branch': return '부본사';
      case 'jisa': return '지사';
      case 'distributor': return '총판';
      case 'agency': return '매장';
      default: return role;
    }
  };

  const formatNumber = (value) => new Intl.NumberFormat('ko-KR').format(Math.floor(value || 0));
  const formatCurrency = (value) => `${formatNumber(value)}원`;
  const formatRateSum = (value) => {
    const n = Number(value) || 0;
    return `${new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 4 }).format(n)}%`;
  };
  const getStatusKey = (row) => `${selectedDate}-${row.id}`;
  const canToggleStatus = ['super', 'admin'].includes(currentUser?.role);

  const companyKeys = useMemo(() => {
    const keys = new Set();
    inputDataList.forEach((row) => keys.add(row.company || '미지정'));
    return Array.from(keys);
  }, [inputDataList]);


  useEffect(() => {
    const initializeDate = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/settlement/inputs/latest-date', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const latestDate = response.data?.date;
        if (latestDate) {
          setSelectedDate(latestDate);
        }
      } catch (err) {
        logger.apiError('GET', '/api/settlement/inputs/latest-date', err);
      } finally {
        setIsInitializingDate(false);
      }
    };

    initializeDate();
  }, []);

  useEffect(() => {
    if (!selectedDate || isInitializingDate) {
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token');
        const [inputRes, rateRes, statusRes] = await Promise.all([
          axios.get(`/api/settlement/inputs?date=${selectedDate}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          axios.get('/api/rates', {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          axios.get(`/api/settlement/statuses?date=${selectedDate}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        setInputDataList(Array.isArray(inputRes.data) ? inputRes.data : []);
        setRates(rateRes.data || []);
        const statusMap = {};
        (Array.isArray(statusRes.data) ? statusRes.data : []).forEach((row) => {
          statusMap[`${selectedDate}-${row.rate_id}`] = Boolean(row.status);
        });
        setSettlementStatuses(statusMap);
      } catch (err) {
        logger.apiError('GET', '/api/settlement/inputs', err);
        setError('일별 정산 데이터를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDate, isInitializingDate]);

  const handleToggleStatus = async (rateRow) => {
    const statusKey = getStatusKey(rateRow);
    const nextValue = !Boolean(settlementStatuses[statusKey]);
    setSettlementStatuses((prev) => ({ ...prev, [statusKey]: nextValue }));
    try {
      const token = localStorage.getItem('token');
      await axios.put('/api/settlement/status', {
        rate_id: rateRow.id,
        settlement_date: selectedDate,
        status: nextValue
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) {
      logger.apiError('PUT', '/api/settlement/status', err);
      setSettlementStatuses((prev) => ({ ...prev, [statusKey]: !nextValue }));
    }
  };

  return (
    <div className="deposit-table-container">
      <div className="daily-settlement-header">
        <div>
          <h2 className="daily-settlement-title">일별 정산</h2>
          <p className="daily-settlement-subtitle">선택한 날짜 기준으로 정산을 확인합니다.</p>
        </div>
        <div className="daily-date-picker">
          <label htmlFor="settlementDate">정산 날짜</label>
          <input
            id="settlementDate"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: '#888', padding: '40px 0' }}>로딩 중...</div>
      ) : error ? (
        <div style={{ textAlign: 'center', color: 'red', padding: '40px 0' }}>{error}</div>
      ) : inputDataList.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#888', padding: '40px 0' }}>
          선택한 날짜의 정산 입력이 없습니다.
        </div>
      ) : (
        <>
          <div className="daily-settlement-grid">
            {companyKeys.map((companyKey) => {
              const inputData = inputDataList.find((row) => (row.company || '미지정') === companyKey);
              const companyRates = rates.filter((rate) => (rate.company || '미지정') === companyKey);
              const getFeeByRole = (role, type) => {
                const adminDef = type === 'deposit' ? 440 : 990;
                const otherDef = 0;
                const get = (v, def) => (Number.isFinite(Number(v)) ? Number(v) : def);
                // 개발(dev)은 본사 건당 수수료를 그대로 사용 (계보도 대시보드와 동일 기준)
                if (role === 'admin' || role === 'dev') return get(type === 'deposit' ? inputData?.deposit_fee_per_count : inputData?.withdrawal_fee_per_count, adminDef);
                if (role === 'major_branch' || role === 'chief_major') return get(type === 'deposit' ? inputData?.deposit_fee_per_count_major_branch : inputData?.withdrawal_fee_per_count_major_branch, otherDef);
                if (role === 'branch' || role === 'representative') return get(type === 'deposit' ? inputData?.deposit_fee_per_count_branch : inputData?.withdrawal_fee_per_count_branch, otherDef);
                if (role === 'jisa') return get(type === 'deposit' ? inputData?.deposit_fee_per_count_jisa : inputData?.withdrawal_fee_per_count_jisa, otherDef);
                if (role === 'distributor') return get(type === 'deposit' ? inputData?.deposit_fee_per_count_distributor : inputData?.withdrawal_fee_per_count_distributor, otherDef);
                if (role === 'agency') return get(type === 'deposit' ? inputData?.deposit_fee_per_count_agency : inputData?.withdrawal_fee_per_count_agency, otherDef);
                return 0;
              };
              const feeEligibleRoles = ['admin', 'dev', 'chief_major', 'representative', 'major_branch', 'branch', 'jisa', 'distributor', 'agency'];
              const computedRows = companyRates.map((rate) => {
                const depositFee = (inputData?.total_deposit_amount || 0) * (Number(rate.rate) || 0) / 100;
                const depositFeePerCount = getFeeByRole(rate.role, 'deposit');
                const withdrawalFeePerCount = getFeeByRole(rate.role, 'withdrawal');
                const depositCountFee = feeEligibleRoles.includes(rate.role)
                  ? (inputData?.total_deposit_count || 0) * depositFeePerCount : 0;
                const withdrawalCountFee = feeEligibleRoles.includes(rate.role)
                  ? (inputData?.total_withdrawal_count || 0) * withdrawalFeePerCount : 0;
                const totalFee = depositFee + depositCountFee + withdrawalCountFee;
                return {
                  ...rate,
                  depositFee,
                  depositCountFee,
                  withdrawalCountFee,
                  totalFee
                };
              });
              const sumDepositFee = computedRows.reduce((s, row) => s + (row.depositFee || 0), 0);
              const sumDepositCountFee = computedRows.reduce((s, row) => s + (row.depositCountFee || 0), 0);
              const sumWithdrawalCountFee = computedRows.reduce((s, row) => s + (row.withdrawalCountFee || 0), 0);
              const sumRate = computedRows.reduce((s, row) => s + (Number(row.rate) || 0), 0);
              const rolesTotalSum = computedRows.reduce((sum, row) => sum + (row.totalFee || 0), 0);

              return (
                <div key={companyKey} className="daily-company-card">
                  <div className="daily-company-header">
                    <div>
                      <span className="daily-company-label">분류</span>
                      <h3 className="daily-company-title">{companyKey}</h3>
                    </div>
                  </div>

                  <div className="result daily-input-summary">
                    <h4>정산 입력 요약</h4>
                    <div className="daily-input-summary-row" role="group" aria-label="정산 입력 요약">
                      <span className="daily-input-summary-badge daily-input-summary-badge--deposit">
                        <span className="daily-input-summary-badge-label">총 입금액</span>
                        <span className="daily-input-summary-badge-value">{formatCurrency(inputData?.total_deposit_amount)}</span>
                      </span>
                      <span className="daily-input-summary-badge daily-input-summary-badge--count">
                        <span className="daily-input-summary-badge-label">총 입금건수</span>
                        <span className="daily-input-summary-badge-value">{formatNumber(inputData?.total_deposit_count)}건</span>
                      </span>
                      <span className="daily-input-summary-badge daily-input-summary-badge--withdraw">
                        <span className="daily-input-summary-badge-label">총 출금액</span>
                        <span className="daily-input-summary-badge-value">{formatCurrency(inputData?.total_withdrawal_amount)}</span>
                      </span>
                      <span className="daily-input-summary-badge daily-input-summary-badge--withdraw">
                        <span className="daily-input-summary-badge-label">총 출금건수</span>
                        <span className="daily-input-summary-badge-value">{formatNumber(inputData?.total_withdrawal_count)}건</span>
                      </span>
                    </div>
                  </div>

                  <table className="deposit-table">
                    <thead>
                      <tr>
                        <th>역할</th>
                        <th>사용자명</th>
                        <th>요율</th>
                        <th>입금 요율 정산액</th>
                        <th>입금건수 수수료</th>
                        <th>출금건수 수수료</th>
                        <th>총 정산액</th>
                        <th className="settlement-status-column">정산 상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {computedRows.length === 0 ? (
                        <tr>
                          <td colSpan={8} style={{ color: '#888' }}>요율 정보가 없습니다.</td>
                        </tr>
                      ) : (
                        computedRows.map((row) => {
                          const statusKey = getStatusKey(row);
                          const isDone = Boolean(settlementStatuses[statusKey]);
                          return (
                          <tr key={row.id}>
                            <td>
                              <span className={`role-badge ${row.role}`}>
                                {getRoleLabel(row.role)}
                              </span>
                            </td>
                            <td>{row.label}</td>
                            <td>{row.rate}%</td>
                            <td>{formatCurrency(row.depositFee)}</td>
                            <td>{formatCurrency(row.depositCountFee)}</td>
                            <td>{formatCurrency(row.withdrawalCountFee)}</td>
                            <td>{formatCurrency(row.totalFee)}</td>
                            <td className="settlement-status-column">
                              <div className="settlement-status">
                                {canToggleStatus ? (
                                  <label className="toggle-switch">
                                    <input
                                      type="checkbox"
                                      checked={isDone}
                                      onChange={() => handleToggleStatus(row)}
                                    />
                                    <span className="toggle-slider" />
                                  </label>
                                ) : null}
                                <span className={`settlement-status-label ${isDone ? 'done' : 'pending'}`}>
                                  {isDone ? '정산 완료' : '미정산'}
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                        })
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="daily-settlement-total-row">
                        <td className="daily-settlement-total-label">합계</td>
                        <td>—</td>
                        <td className="daily-settlement-sum-cell">{formatRateSum(sumRate)}</td>
                        <td className="daily-settlement-sum-cell">{formatCurrency(sumDepositFee)}</td>
                        <td className="daily-settlement-sum-cell">{formatCurrency(sumDepositCountFee)}</td>
                        <td className="daily-settlement-sum-cell">{formatCurrency(sumWithdrawalCountFee)}</td>
                        <td className="daily-settlement-total-amount">{formatCurrency(rolesTotalSum)}</td>
                        <td className="settlement-status-column" />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default DepositTable;
